import Image from 'next/image';
import { type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/utils';

export type MediaHeroProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  'children'
> & {
  /** When empty or whitespace, shows a reserved placeholder block instead of an image. */
  src?: string;
  alt?: string;
  priority?: boolean;
  sizes?: string;
  /** Rendered over the lower part of the image (e.g. a stat callout). */
  footer?: ReactNode;
};

export function MediaHero({
  className,
  src,
  alt,
  priority,
  sizes = '(max-width: 1024px) 100vw, min(720px, 50vw)',
  footer,
  ...props
}: MediaHeroProps) {
  const hasSrc = Boolean(src?.trim());

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-xl border border-border bg-muted shadow-sm',
        className,
      )}
      {...props}
    >
      {hasSrc ? (
        <Image
          src={src!.trim()}
          alt={alt ?? ''}
          width={1200}
          height={900}
          className='aspect-[4/3] w-full object-cover object-center lg:aspect-auto lg:min-h-[28rem]'
          sizes={sizes}
          priority={priority}
        />
      ) : (
        <div
          className='aspect-[4/3] w-full bg-muted lg:min-h-[28rem]'
          aria-hidden
        />
      )}
      {footer ? (
        <>
          <div
            className='pointer-events-none absolute inset-0 bg-gradient-to-t from-background/70 via-transparent to-transparent'
            aria-hidden
          />
          <div className='absolute inset-x-4 bottom-4'>{footer}</div>
        </>
      ) : null}
    </div>
  );
}
